import React, { useCallback, useRef, useState } from 'react';

import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  Divider,
  ListItemText,
  Menu,
  MenuItem,
  Typography,
} from '@mui/material';

import { useConfirmDialog } from '~/components/Common/Providers/ConfirmDialogProvider';

import { useCreateConnectivityAction } from '~/hooks/apis/price-parity/useCreateConnectivityAction';
import { useTriggerExtranetNotification } from '~/hooks/apis/price-parity/useTriggerExtranetNotification';

import featureToggle from '~/utils/featureToggle';

import NextBestActionModal from './NextBestActionModal';

interface Props {
  propertyId: string;
  propertyName: string;
  vendorBk: string | null;
  connectivityActionStatus: string | null;
}

export default function ControlCentreActionButtons({
  propertyId,
  propertyName,
  vendorBk,
  connectivityActionStatus,
}: Props) {
  const showConfirmDialog = useConfirmDialog();
  const createAction = useCreateConnectivityAction();
  const triggerNotification = useTriggerExtranetNotification();

  const anchorRef = useRef<HTMLButtonElement>(null);
  const [menuOpen, setMenuOpen] = useState(false);
  const [notificationDialogOpen, setNotificationDialogOpen] = useState(false);
  const [nextBestActionOpen, setNextBestActionOpen] = useState(false);

  const isNextBestActionEnabled = featureToggle.availableToShow('NEXT_BEST_ACTION_ENABLED');
  const hasExistingAction = connectivityActionStatus !== null;
  const canMutate = vendorBk != null;
  const isLoading = createAction.isPending || triggerNotification.isPending;

  const handleCreateAction = useCallback(async () => {
    setMenuOpen(false);
    if (!canMutate) return;

    const confirmed = await showConfirmDialog({
      title: 'Create Connectivity Action?',
      description: `Are you sure you want to create a new connectivity action for ${propertyName}? This will create a follow-up task for the Connectivity team to address parity issues.`,
    });

    if (!confirmed) return;

    createAction.mutate(propertyId);
  }, [showConfirmDialog, propertyId, propertyName, canMutate, createAction]);

  const handleNotifyPartner = useCallback(() => {
    setMenuOpen(false);
    setNotificationDialogOpen(true);
  }, []);

  const handleNextBestAction = useCallback(() => {
    setMenuOpen(false);
    setNextBestActionOpen(true);
  }, []);

  const handleConfirmNotification = useCallback(() => {
    if (!canMutate) return;
    setNotificationDialogOpen(false);
    triggerNotification.mutate(propertyId);
  }, [propertyId, canMutate, triggerNotification]);

  return (
    <>
      <Button
        ref={anchorRef}
        size="small"
        variant="contained"
        endIcon={<KeyboardArrowDownIcon />}
        onClick={() => setMenuOpen(true)}
        disabled={isLoading}
        sx={{ whiteSpace: 'nowrap' }}
      >
        {isLoading ? 'Processing...' : 'Actions'}
      </Button>

      <Menu
        anchorEl={anchorRef.current}
        open={menuOpen}
        onClose={() => setMenuOpen(false)}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'left' }}
        transformOrigin={{ vertical: 'top', horizontal: 'left' }}
      >
        <MenuItem onClick={handleCreateAction} disabled={!canMutate || hasExistingAction || createAction.isPending}>
          <ListItemText>Create Connectivity Action</ListItemText>
        </MenuItem>
        <MenuItem onClick={handleNotifyPartner} disabled={!canMutate || triggerNotification.isPending}>
          <ListItemText>Notify Partner</ListItemText>
        </MenuItem>
        {isNextBestActionEnabled && <Divider />}
        {isNextBestActionEnabled && (
          <MenuItem onClick={handleNextBestAction} disabled={isLoading}>
            <ListItemText>Next Best Action</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <Dialog open={notificationDialogOpen} onClose={() => setNotificationDialogOpen(false)}>
        <DialogTitle>Trigger Extranet Notification?</DialogTitle>
        <DialogContent sx={{ minWidth: 400 }}>
          <DialogContentText>
            Are you sure you want to send a parity notification to <strong>{propertyName}</strong>?
          </DialogContentText>
          <Typography variant="body2" sx={{ mt: 2 }}>
            This will:
          </Typography>
          <Typography component="ul" variant="body2" sx={{ mt: 0.5, pl: 2 }}>
            <li>Create a connectivity action</li>
            <li>Send an automated notification to the hotel partner via Extranet</li>
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setNotificationDialogOpen(false)}>Cancel</Button>
          <Button variant="contained" onClick={handleConfirmNotification}>
            Send Notification
          </Button>
        </DialogActions>
      </Dialog>

      <NextBestActionModal
        open={nextBestActionOpen}
        onClose={() => setNextBestActionOpen(false)}
        propertyName={propertyName}
      />
    </>
  );
}
